export type RecentProjectVisit = {
  projectId: string
  workspaceSlug: string
  projectName?: string
  lastVisitedAt: string
}

const MAX_STORED = 20
const EVENT_NAME = 'recent-projects-changed'

function storageKey(userId: string) {
  return `recent:projects:${userId}`
}

export function readRecentProjectVisits(userId: string, workspaceSlug?: string): RecentProjectVisit[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter(
        (item): item is RecentProjectVisit =>
          typeof item?.projectId === 'string' &&
          typeof item?.workspaceSlug === 'string' &&
          typeof item?.lastVisitedAt === 'string',
      )
      .filter((item) => !workspaceSlug || item.workspaceSlug === workspaceSlug)
      .sort((a, b) => new Date(b.lastVisitedAt).getTime() - new Date(a.lastVisitedAt).getTime())
  } catch {
    return []
  }
}

export function trackProjectVisit(
  userId: string,
  workspaceSlug: string,
  projectId: string,
  projectName?: string,
) {
  if (typeof window === 'undefined' || !projectId) return

  const existing = readRecentProjectVisits(userId)
  const previous = existing.find(
    (item) => item.projectId === projectId && item.workspaceSlug === workspaceSlug,
  )
  const filtered = existing.filter(
    (item) => !(item.projectId === projectId && item.workspaceSlug === workspaceSlug),
  )
  const visitName = projectName ?? previous?.projectName

  const updated: RecentProjectVisit[] = [
    {
      projectId,
      workspaceSlug,
      ...(visitName ? { projectName: visitName } : {}),
      lastVisitedAt: new Date().toISOString(),
    },
    ...filtered,
  ].slice(0, MAX_STORED)

  window.localStorage.setItem(storageKey(userId), JSON.stringify(updated))
  window.dispatchEvent(new CustomEvent(EVENT_NAME))
}

export function removeRecentProjectVisit(
  userId: string,
  workspaceSlug: string,
  projectId: string,
) {
  if (typeof window === 'undefined' || !projectId) return

  const existing = readRecentProjectVisits(userId)
  const updated = existing.filter(
    (item) => !(item.projectId === projectId && item.workspaceSlug === workspaceSlug),
  )
  if (updated.length === existing.length) return

  window.localStorage.setItem(storageKey(userId), JSON.stringify(updated))
  window.dispatchEvent(new CustomEvent(EVENT_NAME))
}

export function pruneRecentProjectVisits(
  userId: string,
  workspaceSlug: string,
  validProjectIds: Set<string>,
) {
  if (typeof window === 'undefined') return

  const existing = readRecentProjectVisits(userId)
  const updated = existing.filter(
    (item) => item.workspaceSlug !== workspaceSlug || validProjectIds.has(item.projectId),
  )
  if (updated.length === existing.length) return

  window.localStorage.setItem(storageKey(userId), JSON.stringify(updated))
  window.dispatchEvent(new CustomEvent(EVENT_NAME))
}

export function subscribeRecentProjects(callback: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined
  window.addEventListener(EVENT_NAME, callback)
  window.addEventListener('storage', callback)
  return () => {
    window.removeEventListener(EVENT_NAME, callback)
    window.removeEventListener('storage', callback)
  }
}
